<template>
  <v-container class="full-height-container px-6">
    <div class="d-flex align-center flex-column login-container w-100">
      <v-img
        v-if="isLogoBase64 || !customLogo"
        :width="$vuetify.breakpoint.mobile ? 171 : 200"
        :height="$vuetify.breakpoint.mobile ? 60 : 60"
        contain
        :src="
          customLogo
            ? customLogo
            : require(`@/assets/logo${isDark ? '-white' : ''}.svg`)
        "
        class="mb-10"
      ></v-img>
      <div
        v-else
        v-html="customLogo"
        class="mb-10 d-flex"
        :width="$vuetify.breakpoint.mobile ? 171 : 200"
        :height="$vuetify.breakpoint.mobile ? 60 : 60"
      ></div>
      <VerifyEmail
        v-if="emailVerification"
        :email="email"
        :codeSent="verificationCodeSent"
        :skipDashboard="skipDashboard"
        ref="verifyEmailCard"
      />
      <v-card
        v-else-if="!quickRegistering"
        elevation="4"
        class="d-flex v-card--mwidth-600px"
      >
        <v-card-title class="d-flex justify-center">
          <h2 class="font-weight-800 text-center">
            {{ $t("heading.registerPage.registerAccount") }}
          </h2>
        </v-card-title>
        <v-card-text class="pb-8">
          <template v-if="errors.length">
            <collapse-transitions :enabled="false">
              <div
                class="login-message login-message--error mb-6"
                v-for="(error, index) in errors"
                :key="index"
              >
                <div class="login-message__icon-box">
                  <v-icon size="24" color="error">$alertwarning</v-icon>
                </div>
                <div class="login-message__text-box">
                  <h6 class="login-message__title">
                    {{ error }}
                  </h6>
                  <p
                    v-if="alreadyRegisteredError"
                    class="mb-0 p-3 login-message__desc"
                  >
                    {{ $t("general.please") }}
                    <router-link to="/login" class="info--text">
                      {{ $t("general.logInLink") }}
                    </router-link>
                    {{ $t("general.toYourAccount") }}
                    <template v-if="$store.state.home.app.support_link">
                      {{ $t("general.or") }}
                      <a
                        :href="$store.state.home.app.support_link"
                        class="info--text"
                      >
                        {{ $t("general.contactSupportLink") }}
                      </a>
                    </template>
                  </p>
                  <p
                    v-else-if="$store.state.home.app.support_link"
                    class="mb-0 p-3 login-message__desc"
                  >
                    {{ $t("message.support.before") }}
                    <a
                      :href="$store.state.home.app.support_link"
                      class="info--text"
                      >{{ $t("message.support.link") }}</a
                    >
                    {{ $t("message.support.after") }}
                  </p>
                </div>
              </div>
            </collapse-transitions>
          </template>
          <template v-if="setPassword">
            <collapse-transitions enabled>
              <div class="login-message login-message--success mb-6">
                <div class="login-message__icon-box">
                  <v-icon size="24" color="success">$infoCircle</v-icon>
                </div>
                <div class="login-message__text-box">
                  <h6 class="login-message__title">
                    {{ $t("heading.loginPage.setPassword.title") }}
                  </h6>
                  <p class="mb-0 p-3 login-message__desc">
                    {{ $t("heading.loginPage.setPassword.text") }}
                  </p>
                </div>
              </div>
            </collapse-transitions>
          </template>
          <v-row
            v-if="authProviders && authProviders.length"
            class="social-login-btns social-login-btns--register row--small"
          >
            <v-col
              v-for="provider in authProviders"
              :key="provider"
              class="col col-12 social-login-btn-col"
              :class="authProviders.length == 1 ? '' : 'col-sm-6'"
            >
              <v-btn
                class="social-login-btn"
                @click.prevent="socialAuth(provider)"
                :loading="authLoading"
                block
                large
                elevation="0"
                outlined
                color="gray"
              >
                <v-icon :class="`social-icon-${provider}`">${{ provider }}</v-icon>
                <span class="text--gray-darken2 font-weight-400 p-sm"
                  >{{ $t("heading.registerPage.signInWith") }}
                  {{
                    provider.charAt(0).toUpperCase() + provider.slice(1)
                  }}</span
                >
              </v-btn>
            </v-col>
          </v-row>
          <div
            v-if="authProviders && authProviders.length"
            class="or-divider mt-6 mb-6"
          >
            <div>{{ $t("heading.registerPage.fillForm") }}</div>
          </div>
          <v-form class="form" ref="form" @submit.prevent>
            <template v-if="setPassword">
              <v-text-field
                outlined
                :label="$t('form.label.password')"
                type="password"
                v-model="password"
                :rules="registerPasswordRules"
                solo
                flat
                hide-details="auto"
                validate-on-blur
                autocomplete="new-password"
                name="new-password"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="headings--text">$lockoutline</v-icon>
                </template>
              </v-text-field>
              <v-text-field
                outlined
                :label="$t('form.label.passwordConfirm')"
                type="password"
                v-model="passwordConfirm"
                :rules="registerPasswordRules"
                solo
                flat
                hide-details="auto"
                validate-on-blur
                autocomplete="new-password"
                name="confirm-password"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="headings--text">$lockoutline</v-icon>
                </template>
              </v-text-field>
            </template>
            <v-text-field
              v-else
              outlined
              :label="$t('form.label.emailAddress')"
              v-model="email"
              :rules="emailRules"
              large
              solo
              flat
              hide-details="auto"
              validate-on-blur
              autocomplete="email"
              name="email"
            >
              <template v-slot:prepend-inner>
                <v-icon color="headings--text">$email</v-icon>
              </template>
            </v-text-field>
          </v-form>

          <div class="d-flex justify-space-between align-center">
            <div class="d-flex align-center flex-wrap">
              <div class="mr-1">
                {{ $t("heading.registerPage.registeringAccept") }}
              </div>
              <RegistrationTermsModal>
                <template #activator="{ on, attrs }">
                  <div>
                    <a v-bind="attrs" v-on="on">{{
                      $t("heading.registerPage.termsOfService")
                    }}</a>
                  </div>
                </template>
              </RegistrationTermsModal>
            </div>
          </div>

          <v-btn
            large
            @click.prevent="register()"
            color="primary"
            block
            elevation="0"
            class="mt-6"
            :loading="registering"
          >
            <span class="font-weight-600">{{ $t("button.register") }}</span>

            <template v-slot:loader>
              <loader :color="'white'" :size="24" />
            </template>
          </v-btn>
        </v-card-text>
        <div class="login-bottom-box login-bottom-box--register p-d text--base">
          <div>{{ $t("heading.registerPage.alreadyRegistered") }}</div>
          <div>
            <router-link to="/login" class="ml-1 mr-1">
              {{ $t("button.signIn") }}</router-link
            >
            {{ $t("general.or") }}
            <router-link to="/forgot-password" class="ml-1">
              {{ $t("button.resetPassword") }}
            </router-link>
          </div>
        </div>
      </v-card>
      <v-card
        v-else
        elevation="4"
        class="d-flex v-card--mwidth-600px v-card--minheight-508px"
      >
        <v-card-text
          class="d-flex justify-center align-center flex-column flex-grow-1 pb-0"
        >
          <svg
            v-if="quickRegisteringCompleted"
            width="114"
            height="114"
            viewBox="0 0 114 114"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="57"
              cy="57"
              r="51"
              stroke="var(--v-primary-base)"
              stroke-width="12"
            />
            <path
              d="M37.5 57.5635L52.75 72.8135L80.25 45.3135"
              stroke="var(--v-primary-base)"
              stroke-width="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <loader v-else :color="'primary'" :size="118" :width="12">
            <template #default>
              <v-icon color="primary" size="30">$loadingHourglass</v-icon>
            </template>
          </loader>
          <v-card-title class="d-flex justify-center">
            <h2 class="font-weight-800">
              {{ $t("heading.registerPage.creatingAccount.title") }}
            </h2>
          </v-card-title>
          <div class="p-lg text-center text--gray-darken1">
            {{ $t("heading.registerPage.creatingAccount.text") }}
          </div>
        </v-card-text>
      </v-card>
      <LanguageSwitcher isLogin class="mt-10" />
    </div>
    <v-img
      :src="require('@/assets/illustrations/radio-lines.svg')"
      class="radio-lines"
    />
  </v-container>
</template>

<script>
import Api from "@/apis/Api";
import Auth from "../apis/Auth";
import FormRulesMixin from "../mixins/FormRulesMixin";
import Loader from "../components/Loader.vue";
import CollapseTransitions from "../components/transitions/CollapseTransitions.vue";
import LanguageSwitcher from "../components/LanguageSwitcher.vue";
import VerifyEmail from "./VerifyEmail.vue";
import RegistrationTermsModal from "../components/modal/RegistrationTermsModal.vue";

export default {
  components: {
    CollapseTransitions,
    Loader,
    LanguageSwitcher,
    VerifyEmail,
    RegistrationTermsModal,
  },
  mixins: [FormRulesMixin],
  props: {
    predefinedEmail: {
      type: String,
      default: "",
    },
  },
  data: function () {
    return {
      email: "",
      emailConfirm: "",
      password: "",
      passwordConfirm: "",
      errors: [],
      alreadyRegisteredError: false,
      registering: false,
      quickRegistering: false,
      quickRegisteringCompleted: false,
      authLoading: false,
      emailVerification: false,
      verificationCodeSent: false,
      setPassword: false,
      skipDashboard: false,
    };
  },
  beforeRouteEnter: async (__, _, next) => {
    await Auth.remoteAuthCheck().then((response) => {
      if (response === true) {
        next((vm) => {
          vm.$root.$emit("loggedIn", true);
          next(vm.$route.query.redirect || "/");
        });
      } else {
        next();
      }
    });
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.dark;
    },
    autoCompleteEmail() {
      if (this.predefinedEmail) {
        return this.predefinedEmail;
      }
      if (this.$route.query.email) {
        return this.$route.query.email;
      }
      return "";
    },
    isCodeComplete() {
      return this.verificationCodeSplit.every((digit) => digit !== "");
    },
    customLogo: function () {
      if (this.$vuetify.theme.dark && this.$store.state.ThemeModule.darkLogo) {
        return this.$store.state.ThemeModule.darkLogo;
      }

      if (
        !this.$vuetify.theme.dark &&
        this.$store.state.ThemeModule.lightLogo
      ) {
        return this.$store.state.ThemeModule.lightLogo;
      }

      return false;
    },
    isLogoBase64: function () {
      return (
        this.customLogo && this.customLogo.trim().startsWith("data:image/")
      );
    },
    authProviders() {
      return this.$store.state.home.app.integrations.auth;
    },
    registrationEnabled() {
      if (this.$store.state.home?.app?.registration_enabled) {
        return true;
      }
      return false;
    },
  },
  mounted: function () {
    if (Object.prototype.hasOwnProperty.call(this.$route.query, "control-panel-upgrade")) {
      localStorage.setItem(window.location.origin + "-redirect", "control-panel-upgrade");
      localStorage.setItem(window.location.origin + "-token", this.$route.query.token);
      this.skipDashboard = true;

      if (this.autoCompleteEmail) {
        this.quickRegistering = true;
      }

      return Auth.loadRecaptchaScript().then(() => {
        if (this.autoCompleteEmail) {
          this.email = this.autoCompleteEmail;
          this.$nextTick(() => {
            this.quickRegistering = true;
            this.register();
          });
        }
      });
    }

    if (!this.registrationEnabled) {
      this.$router.push({ path: "/login" });
      return;
    }
    document.addEventListener("keyup", this.handleEnter);
    if (Object.prototype.hasOwnProperty.call(this.$route.query, "new-site")) {
      localStorage.setItem(window.location.origin + "-redirect", "new-site");
      this.skipDashboard = true;
    } else if (
      Object.prototype.hasOwnProperty.call(this.$route.query, "new-import")
    ) {
      localStorage.setItem(window.location.origin + "-redirect", "new-import");
    }
    if (this.autoCompleteEmail) {
      this.quickRegistering = true;
    }

    Auth.loadRecaptchaScript().then(() => {
      if (this.autoCompleteEmail) {
        this.email = this.autoCompleteEmail;
        this.$nextTick(() => {
          this.quickRegistering = true;
          this.register();
        });
      }
    });
  },
  destroyed: function () {
    document.removeEventListener("keyup", this.handleEnter);
  },

  methods: {
    handleEnter(e) {
      if (e.code === "Enter" || e.code === "NumpadEnter") {
        this.register();
      }
    },
    register() {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      // if (Object.prototype.hasOwnProperty.call(this.$route.query, "new-site")) {

      // } else {
      // if (!this.$refs.form.validate()) return;
      // }
      this.errors = [];
      this.registering = true;
      if (this.skipDashboard) {
        this.quickRegistering = true;
      }
      Auth.register(this.email, this.password)
        .then((response) => {
          if (response.data.set_password == true) {
            this.setPassword = true;
            this.registering = false;
            this.quickRegistering = false;
            return;
          }
          if (response.data.verify_email == true) {
            this.verificationCodeSent = true;
            this.emailVerification = true;
            document.removeEventListener("keyup", this.handleEnter);
            this.registering = false;
            this.quickRegistering = false;
            return;
          }
          this.$root.$emit("loggedIn", true);
          Auth.hideRecaptchaBadge();

          if (response.data.after_register_redirect_url) {
            this.skipDashboard = true;
            this.quickRegisteringCompleted = true;
            window.location = response.data.after_register_redirect_url;
            return;
          }
          if (!this.skipDashboard) {
            this.$router.push({ path: this.$route.query.redirect || "/" });
          } else {
            this.quickRegisteringCompleted = true;
          }
          this.$root.$emit("after-login");
        })
        .catch((error) => {
          if (error.response.data.error_type == "email_already_registered") {
            this.alreadyRegisteredError = true;
          }
          this.errors = Api.getErrorMessages(error);
          this.registering = false;
          this.quickRegistering = false;
        })
        .finally(() => {
          if (!this.skipDashboard) {
            this.registering = false;
            this.quickRegistering = false;
          }
        });
    },

    socialAuth(provider) {
      let url = `/auth/${provider}/redirect-url`;
      if (localStorage.getItem(window.location.origin + "-token")) {
        url += '?token=' + localStorage.getItem(window.location.origin + "-token");
      }

      this.authLoading = true;
      Api.get(url)
        .then((response) => {
          window.location = response.data.data.url;
        })
        .catch((error) => {
          this.errors = [Api.getErrorMessage(error)];
        })
        .finally(() => {
          this.authLoading = false;
        });
    },
  },
};
</script>

<style src="./Login.scss" lang="scss" scoped />
